import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import AdminDataManager from "./AdminDataManager";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  Map,
  Shield,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  FileCheck,
  Building,
  Eye,
  Database,
  FileText,
  Vote,
  Gavel,
  BookOpen,
  MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Items
const sidebarItems = [
  {
    section: "Admin",
    items: [
      { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
      { name: "Users", path: "/admin/users", icon: Users },
      { name: "Lands", path: "/admin/lands", icon: Map },
      { name: "Consortiums", path: "/admin/consortiums", icon: Building },
    ],
  },
  {
    section: "Data Manager",
    items: [
      { name: "All Collections", path: "/admin/data/users", icon: Database },
      { name: "Agreements", path: "/admin/data/agreements", icon: FileText },
      { name: "Meetings", path: "/admin/data/meetings", icon: Clock },
      { name: "Voting Agendas", path: "/admin/data/voting_agendas", icon: Vote },
      { name: "RFPs", path: "/admin/data/rfps", icon: Gavel },
      { name: "Bids", path: "/admin/data/bids", icon: FileCheck },
      { name: "Consents", path: "/admin/data/consents", icon: Shield },
      { name: "Knowledge Articles", path: "/admin/data/knowledge_articles", icon: BookOpen },
      { name: "Map Zones", path: "/admin/data/map_zones", icon: MapPin },
    ],
  },
];

// Admin Dashboard Overview
const AdminOverview = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/admin`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch admin dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
      </div>
    );
  }

  const stats = [
    {
      title: "Total Users",
      value: dashboard?.stats?.total_users || 0,
      icon: Users,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Total Lands",
      value: dashboard?.stats?.total_lands || 0,
      icon: Map,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Pending Verifications",
      value: dashboard?.pending_verifications || 0,
      icon: Clock,
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      title: "Active Consortiums",
      value: dashboard?.active_consortiums || 0,
      icon: Building,
      color: "bg-blue-100 text-[#4E80FF]",
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Admin Dashboard
        </h1>
        <p className="text-slate-600">
          Platform overview and management controls
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                  <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div
                  className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
                >
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Lands */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Recent Lands</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_lands?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_lands.slice(0, 5).map((land) => (
                  <div
                    key={land.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                        <Map className="w-4 h-4 text-[#4E80FF]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900">
                          {land.survey_number}
                        </p>
                        <p className="text-xs text-slate-500">{land.location}</p>
                      </div>
                    </div>
                    <Badge
                      variant={land.status === "pending" ? "secondary" : "default"}
                      className={land.status === "verified" ? "bg-[#4E80FF]" : ""}
                    >
                      {land.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No recent lands</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Consortiums */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              Recent Consortiums
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_consortiums?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_consortiums.slice(0, 5).map((consortium) => (
                  <div
                    key={consortium.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                        <Building className="w-4 h-4 text-[#4E80FF]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900">
                          {consortium.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {consortium.members?.length || 0} members
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {consortium.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">
                No recent consortiums
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Users Management
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          User Management
        </h1>
        <p className="text-slate-600">View and manage all platform users</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-[#4E80FF]">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Admin Lands Management
const AdminLands = () => {
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLands();
  }, []);

  const fetchLands = async () => {
    try {
      const response = await axios.get(`${API}/lands`);
      setLands(response.data);
    } catch (error) {
      console.error("Failed to fetch lands:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (landId) => {
    try {
      await axios.put(`${API}/lands/${landId}/verify`);
      toast.success("Land verified successfully");
      fetchLands();
    } catch (error) {
      toast.error("Failed to verify land");
    }
  };

  const handleEstimate = async (landId) => {
    try {
      const response = await axios.put(`${API}/lands/${landId}/estimate`);
      toast.success(
        `Estimation complete: ₹${(response.data.estimated_value / 100000).toFixed(1)}L`
      );
      fetchLands();
    } catch (error) {
      toast.error("Failed to estimate land value");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-lands-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Land Management
        </h1>
        <p className="text-slate-600">Verify and manage land submissions</p>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="verified">Verified</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <LandTable
            lands={lands.filter((l) => l.status === "pending")}
            loading={loading}
            onVerify={handleVerify}
            onEstimate={handleEstimate}
          />
        </TabsContent>

        <TabsContent value="verified" className="mt-4">
          <LandTable
            lands={lands.filter(
              (l) => l.status === "verified" || l.status === "eligible"
            )}
            loading={loading}
            onVerify={handleVerify}
            onEstimate={handleEstimate}
          />
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <LandTable
            lands={lands}
            loading={loading}
            onVerify={handleVerify}
            onEstimate={handleEstimate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Land Table Component
const LandTable = ({ lands, loading, onVerify, onEstimate }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
      </div>
    );
  }

  if (lands.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-16 text-center">
          <Map className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No lands found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Survey No.</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Area (sqm)</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Est. Value</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lands.map((land) => (
              <TableRow key={land.id}>
                <TableCell className="font-mono font-medium">
                  {land.survey_number}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{land.location}</p>
                    <p className="text-sm text-slate-500">
                      {land.district}, {land.state}
                    </p>
                  </div>
                </TableCell>
                <TableCell>{land.area_sqm?.toLocaleString()}</TableCell>
                <TableCell className="capitalize">{land.land_type}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      land.status === "pending"
                        ? "secondary"
                        : land.status === "verified"
                        ? "default"
                        : "outline"
                    }
                    className={
                      land.status === "verified" || land.status === "eligible"
                        ? "bg-[#4E80FF]"
                        : ""
                    }
                  >
                    {land.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {land.estimated_value
                    ? `₹${(land.estimated_value / 100000).toFixed(1)}L`
                    : "-"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {land.status === "pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onVerify(land.id)}
                        data-testid={`verify-land-${land.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verify
                      </Button>
                    )}
                    {land.status === "verified" && !land.estimated_value && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEstimate(land.id)}
                        data-testid={`estimate-land-${land.id}`}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Estimate
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// Admin Consortiums Management
const AdminConsortiums = () => {
  const navigate = useNavigate();
  const [consortiums, setConsortiums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsortiums();
  }, []);

  const fetchConsortiums = async () => {
    try {
      const response = await axios.get(`${API}/consortiums`);
      setConsortiums(response.data);
    } catch (error) {
      console.error("Failed to fetch consortiums:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-consortiums-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Consortium Management
        </h1>
        <p className="text-slate-600">View and manage all consortiums</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
      ) : consortiums.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Total Area</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consortiums.map((consortium) => (
                  <TableRow key={consortium.id}>
                    <TableCell className="font-medium">{consortium.name}</TableCell>
                    <TableCell>{consortium.location}</TableCell>
                    <TableCell>{consortium.members?.length || 0}</TableCell>
                    <TableCell>
                      {consortium.total_area_sqm?.toLocaleString() || 0} sqm
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {consortium.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/consortium/${consortium.id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Building className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No consortiums found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="lands" element={<AdminLands />} />
        <Route path="consortiums" element={<AdminConsortiums />} />
        <Route path="data/:collection" element={<AdminDataManager />} />
      </Routes>
    </DashboardLayout>
  );
};

export default AdminDashboard;
